import { AccountTableRowStatus } from './constants'

export type AccountId = string

export type Account = {
  id: AccountId
  number: string
  initialMappedNumber?: string
  mapped: boolean
  mappedNumber: string
  type: string
  accountType?: string
  accountTypeExplicitlySet?: boolean
  remappedByUser?: boolean
  externallyMappedNumber?: string
} & LocalisedOriginalNames &
  LocalisedCustomNames &
  LocalisedNames

export type LocalisedNamesOrOriginalNames =
  | LocalisedNames
  | LocalisedOriginalNames

export type ReservedPostfixes = Record<AccountRange, Array<number>>

export function isAccount(item: Account | Group): item is Account {
  return 'number' in item
}

export function isGroup(item: Account | Group): item is Group {
  return 'accountRange' in item
}

export type RawAccount = Account & {
  mapped: boolean
  originalNumber: string
}

export type Accounts = Record<AccountId, Account>

export type AccountDataToSave = {
  key: string
  value: string
}

export type AccountRange = string

export type AccountBalance = {
  value: string
  formattedValue: string
}

export type AccountTableRow = Account & {
  mappedAccount: GroupMappedAccountAssociation & Group
  mappedAccountNumber: AccountRange
  mappedAccountType: string
  balance: AccountBalance
  sortableBalance: number
  recommendation: AIRecommendation
  status: AccountTableRowStatus
  isLowConfidence: boolean
  accountTypeExistsInMappingList?: boolean
}

export type Locale =
  | 'fr'
  | 'en'
  | 'nl'
  | 'de'
  | 'da'
  | 'es'
  | 'fi'
  | 'it'
  | 'nb'
  | 'pl'
  | 'se'
  | 'cs'
  | 'ar'
  | 'az'
  | 'bs'
  | 'bg'
  | 'el'
  | 'et'
  | 'hr'
  | 'hu'
  | 'id'
  | 'is'
  | 'ja'
  | 'ka'
  | 'kk'
  | 'km'
  | 'ko'
  | 'lt'
  | 'lv'
  | 'pt'
  | 'ro'
  | 'ru'
  | 'sk'
  | 'sl'
  | 'sr'
  | 'th'
  | 'tr'
  | 'uk'
  | 'vi'
  | 'zh'
  | 'ca'

export type CapitalisedLocale = Capitalize<Locale>

export type LocalisedNames = {
  [key in CapitalisedLocale as `name${key}`]: string
}

export type LocalisedOriginalNames = {
  [key in CapitalisedLocale as `originalName${key}`]: string
}

export type LocalisedCustomNames = {
  [key in CapitalisedLocale as `customName${key}`]: string
}

export type GroupHierarchyEntry = {
  children: Array<GroupId>
  level: number
  parent: GroupId
}

export type GroupId = string

export type GroupHierarchy = Record<GroupId, GroupHierarchyEntry>

export type GroupCollapse = Record<GroupId, boolean>

export type Group = {
  accountRange: AccountRange
  type: string
} & LocalisedNames

export type RawGroupLeaf = Pick<
  RawAccount,
  'id' | keyof LocalisedNames | 'number' | 'originalNumber' | 'type'
> & {
  accountType?: string
}

export type RawGroup = {
  id: GroupId
  name: string
  type: string
  children: Array<RawGroup | RawGroupLeaf>
}

export type Groups = Record<GroupId, Group>

export type GroupMappedAccountAssociation = {
  mappedNumber: string
  isSaved: boolean
  groupId?: GroupId
}

export type MappedAccountsOfGroup = Record<
  AccountId,
  GroupMappedAccountAssociation
>

export type MappedAccountsPerRange = Record<AccountRange, MappedAccountsOfGroup>

export type GroupMappedAccounts = Record<GroupId, MappedAccountsOfGroup>
export type MappedAccountIds = Record<AccountId, GroupId>

export type MappingProposal = Pick<Account, keyof LocalisedNames | 'number'> & {
  accountType?: string
}

export type AccountRangePerGroup = Record<AccountRange, GroupId>

export type PreparedSearchGroupEntry = {
  itemId: GroupId
  order: number
  type: 'group'
  preparedAccountRange: Fuzzysort.Prepared
  preparedName: Fuzzysort.Prepared
}

export type PreparedSearchAccountEntry = {
  itemId: AccountId
  order: number
  type: 'account'
  preparedNumber: Fuzzysort.Prepared
  preparedOriginalName: Fuzzysort.Prepared
}

export type PreparedSearchEntry =
  | PreparedSearchGroupEntry
  | PreparedSearchAccountEntry

export type SearchResult = {
  type: 'account' | 'group'
  order: number
  highlights: {
    preparedName: Omit<Fuzzysort.KeyResult<PreparedSearchEntry>, 'obj'> | null
    preparedAccountRange: Omit<
      Fuzzysort.KeyResult<PreparedSearchEntry>,
      'obj'
    > | null
    preparedOriginalName: Omit<
      Fuzzysort.KeyResult<PreparedSearchEntry>,
      'obj'
    > | null
    preparedNumber: Omit<Fuzzysort.KeyResult<PreparedSearchEntry>, 'obj'> | null
  }
}

export type SearchResultsByGroupId = Record<GroupId, SearchResult>

// predicates
export const isRawGroup = (item: RawGroup | RawGroupLeaf): item is RawGroup =>
  item.type === 'group'

// Store action payloads
export type AIRecommendation = {
  id: AccountId
  proposals: Array<string>
  low_confidence: boolean
}

type DigestRecommendationsSuccessPayload = {
  accounts: AIRecommendation[]
  status: 'success'
}

export type ErrorMessagesPerLocale = {
  en: string
  fr: string
  nl: string
}

export type DigestRecommendationsFailurePayload = {
  error_message: string
  error_messages_per_locale?: ErrorMessagesPerLocale
  status: 'error'
}

export type DigestRecommendationsPayload =
  | DigestRecommendationsSuccessPayload
  | DigestRecommendationsFailurePayload

// Store action payload predicates
export const isDigestRecommendationsSuccessPayload = (
  payload:
    | DigestRecommendationsSuccessPayload
    | DigestRecommendationsFailurePayload
): payload is DigestRecommendationsSuccessPayload =>
  payload.status === 'success'

export type SaveMappingError = {
  body: SaveMappingResponse
}

export type SaveMappingResponse = {
  errors?: string[]
  nextPath?: string
}
